import MarkdownIt from 'markdown-it';
import katex from 'katex';

// Answers are Markdown with LaTeX and our own document / element references
// mixed in. Two things must never reach the Markdown parser:
//
//   LaTeX  - `\neq` would be eaten by the newline normalization, `&` is how a
//            matrix aligns its rows, and `*` between two formulas reads as
//            emphasis.
//   Refs   - `[Doc: x, page 4](url)` is a valid Markdown link, so the parser
//            would render a plain <a> and the click handling, the page-range
//            label and the 3D element wiring would all be lost.
//
// Both are lifted out first, the parser sees inert placeholders, and the real
// markup is put back afterwards.

// Private-use characters: markdown-it rewrites NUL to U+FFFD for safety, and no
// Markdown rule can match a run of PUA code points.
const PH_OPEN = '\uE000';
const PH_CLOSE = '\uE001';
const PLACEHOLDER_PATTERN = new RegExp(PH_OPEN + '(\\d+)' + PH_CLOSE, 'g');

// Longest delimiters first so $$ wins over $ and \[ over \(. Only complete
// pairs match, so a half-streamed "$$ t = \frac{1" stays literal text until its
// closing delimiter arrives instead of flashing a KaTeX error. The inline rule
// refuses to open on whitespace or another $, and to close on whitespace or
// before a digit, which keeps prices ("costs $5 and $10") out of math mode. The
// closing unit may itself be an escape, so a formula can end on one ($75\%$).
const MATH_PATTERN = new RegExp([
  /\$\$((?:\\[\s\S]|[^\\])+?)\$\$/,   // $$ ... $$
  /\\\[([\s\S]+?)\\\]/,               // \[ ... \]
  /\\\(([\s\S]+?)\\\)/,               // \( ... \)
  /\$(?!\s|\$)((?:\\.|[^$\\\n])*?(?:\\.|[^\s$\\]))\$(?!\d)/, // $ ... $
].map((r) => r.source).join('|'), 'g');

const DISPLAY_BY_GROUP = { 1: true, 2: true, 3: false, 4: false };

// Document citations, per the format the AI server documents. The filename
// keeps its own language, spaces and case. After `Doc:` any filename is
// accepted; without that prefix it must end in a document extension, which is
// what keeps an ordinary bracketed phrase from matching. Then either
//   - a spreadsheet location: `sheet <name>, row(s) <numbers>`, or
//   - pages: `page(s) <numbers>`, where the word itself may be missing.
// Numbers are a single value, a range (hyphen or en dash), or any
// comma-separated mix of the two. `Doc:`, `sheet`, `row` and `page` are always
// English, even in a Hebrew answer.
//
//   [Doc: Specification.pdf, pages 3–5](url)
//   [Doc: BOQ.xlsx, sheet Electrical, rows 12-40](url)
//   [BOQ.xlsx, sheet Electrical, row 12]
//
// Both the URL and the outer parentheses are optional - a verified citation
// with no unambiguous URL arrives bare. The URL is matched loosely rather than
// as https?:// because our own backend rewrites citations to the relative
// `/apis/docs/:id/file` presigning route.
//
// Groups: 1 file after `Doc:`, 2 bare file, 3 sheet, 4 rows, 5 pages, 6 url.
const DOC_NUMBERS = String.raw`\d+(?:\s*[-–]\s*\d+)?(?:\s*,\s*\d+(?:\s*[-–]\s*\d+)?)*`;
const DOC_REF_PATTERN = new RegExp(
  String.raw`\(?\[(?:Doc:\s*([^[\]\r\n]+?)|([^[\]\r\n]+?\.(?:pdf|docx?|txt|xlsx?|xlsm|csv|tsv))),\s*` +
  String.raw`(?:sheet\s+([^[\]\r\n]+?),\s*rows?\s+(${DOC_NUMBERS})|(?:pages?\s+)?(${DOC_NUMBERS}))` +
  String.raw`\s*\](?:\(([^\s()]+)\))?\)?`,
  'gi'
);

// Emitted when the server could not verify a citation it wanted to make.
const UNVERIFIED_REF_PATTERN = /\[unverified document citation\]/gi;

const REF_PATTERNS = [
  { re: UNVERIFIED_REF_PATTERN, build: () => UNVERIFIED_REF_HTML },
  { re: DOC_REF_PATTERN, build: (m) => toReplaceDocLinks({ source: m[1] || m[2], sheet: m[3], rows: m[4], pages: m[5], url: m[6] }) },
  { re: /\(\[Obj:\s*([^\]]+)\]\(([^)]+)\)\)/gi, build: (m) => toReplaceElementLinks(m[0], m[1], m[2]) },
  { re: /\[Obj:\s*([^\]]+)\]\(([^)]+)\)/gi,       build: (m) => toReplaceElementLinks(m[0], m[1], m[2]) },
];

// `html: false` escapes any raw HTML in the answer and the built-in link
// validation rejects javascript:/vbscript:/data: urls, so the parser's output
// needs no separate sanitizer. Everything injected afterwards is ours.
const md = new MarkdownIt({ html: false, breaks: true, linkify: false, typographer: false });

const renderToken = (tokens, idx, options, env, self) => self.renderToken(tokens, idx, options);
const defaultLinkOpen = md.renderer.rules.link_open || renderToken;
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet('target', '_blank');
  tokens[idx].attrSet('rel', 'noopener noreferrer');
  return defaultLinkOpen(tokens, idx, options, env, self);
};

// Answers mix Hebrew and English freely, so direction is decided per block
// rather than once for the whole answer: `dir="auto"` reads the first strong
// character of the element it sits on, and one on the wrapper would let a
// leading "**", digit or Latin term set the direction for everything after it.
['paragraph_open', 'heading_open', 'bullet_list_open', 'ordered_list_open', 'list_item_open',
  'blockquote_open', 'th_open', 'td_open', 'link_open']
  .forEach((rule) => {
    const previous = md.renderer.rules[rule] || renderToken;
    md.renderer.rules[rule] = (tokens, idx, options, env, self) => {
      tokens[idx].attrSet('dir', 'auto');
      // Column alignment arrives as physical left/right, which fights an RTL
      // cell: the assistant writes ":---" for every column by convention, not
      // to ask for physical left. Logical start/end follows the direction.
      const style = tokens[idx].attrGet('style');
      if (style) {
        tokens[idx].attrSet('style', style
          .replace('text-align:left', 'text-align:start')
          .replace('text-align:right', 'text-align:end'));
      }
      return previous(tokens, idx, options, env, self);
    };
  });

// A wide table scrolls inside its own box rather than stretching the chat. The
// table takes its direction from its first cell, so an all-Hebrew table reads
// right to left, columns included.
md.renderer.rules.table_open = () => '<div class="chat-table-scroll"><table dir="auto">';
md.renderer.rules.table_close = () => '</table></div>';

// Indented code blocks off: the assistant indents to show nesting, not to mark
// code, and four leading spaces used to render as ordinary text. Fenced blocks
// (```) still work, which is the explicit way to ask for code.
md.disable('code');

// Pulls the formulas and references out, leaving numbered placeholders behind.
function extractProtected(text) {
  const items = [];
  const stash = (item) => {
    items.push(item);
    return `${PH_OPEN}${items.length - 1}${PH_CLOSE}`;
  };

  // Math first: a reference written inside a formula belongs to the formula.
  let out = text.replace(MATH_PATTERN, (...args) => {
    const groups = args.slice(1, 5);
    const index = groups.findIndex((value) => value !== undefined);
    if (index === -1) return args[0];
    return stash({ type: 'math', tex: groups[index], display: DISPLAY_BY_GROUP[index + 1], raw: args[0] });
  });

  REF_PATTERNS.forEach(({ re, build }) => {
    out = out.replace(re, (...args) => {
      const match = args.slice(0, -2);
      return stash({ type: 'html', html: build(match), raw: match[0] });
    });
  });

  return { text: out, items };
}

function restore(text, items, render) {
  if (!items.length) return text;
  return text.replace(PLACEHOLDER_PATTERN, (whole, index) => {
    const item = items[Number(index)];
    return item ? render(item) : whole;
  });
}

// `trust` is off, so \href and \html* are refused and the output is safe to
// inject as-is. `throwOnError` renders the offending source in red rather than
// taking the whole answer down.
function renderMathHtml({ tex, display }) {
  const html = katex.renderToString(tex, {
    displayMode: display,
    throwOnError: false,
    trust: false,
    strict: 'ignore',
  });
  // Custom elements, because they cannot nest inside KaTeX's own spans or be
  // closed early by a <p>: that makes them unambiguous to match again when the
  // typewriter needs to treat a formula as a single step.
  return display
    ? `<chat-math-block dir="ltr">${html}</chat-math-block>`
    : `<chat-math dir="ltr">${html}</chat-math>`;
}

const toHtml = (item) => (item.type === 'math' ? renderMathHtml(item) : item.html);
const toRawText = (item) => item.raw;

// Backend/AI text can carry newlines as the literal characters "\n" (stream
// frames, stored history, the done answer). Only ever run this on text that has
// had its math lifted out, or it will eat LaTeX commands beginning with "n".
const normalizeNewlines = (text) => text.replace(/\\n/g, '\n');

// The assistant bullets with "\u2022" as often as with "*", and a bullet
// character is not a Markdown list marker. Swap a leading one for a real marker
// so those lines become a list instead of a paragraph of glyphs.
const normalizeBullets = (text) => text.replace(/^([ \t]*)\u2022[ \t]+/gm, '$1- ');

// Real newlines, LaTeX and reference syntax left intact - for the clipboard,
// where the source is more useful than rendered markup.
export function normalizeChatText(text) {
  const { text: stripped, items } = extractProtected(String(text ?? ''));
  return restore(normalizeNewlines(stripped), items, toRawText);
}

export function formatBotMessage(text) {
  const { text: stripped, items } = extractProtected(String(text ?? ''));
  const html = md.render(normalizeBullets(normalizeNewlines(stripped)));
  return restore(html, items, toHtml);
}

// Escapes a value being interpolated into our own reference markup. The
// Markdown parser escapes everything else it emits.
function sanitizeText(text) {
  return String(text ?? '').replace(/[&<>"']/g, function (match) {
    const escape = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    };
    return escape[match];
  });
}


//--- To Replace chat Texts functions

// The filename is passed through verbatim: the viewer picks its renderer from
// the extension and offers a download for anything it cannot display, so which
// types are supported is its business, not ours. (This used to append ".pdf"
// unconditionally, which turned every Report.docx into Report.docx.pdf.)
//
// Trimming the extension is for the label alone, and deliberately has no list
// of types behind it. The trailing segment must contain a letter, so a document
// numbered 511.005 keeps its number while 511.005.pdf loses only the ".pdf".
const stripExtension = (name) => name.replace(
  /\.[A-Za-z0-9]{1,5}$/,
  (ext) => (/[A-Za-z]/.test(ext) ? '' : ext)
);

// "3 – 5, 8" -> "3-5, 8" for the label (pages or rows), and the first page or
// row for the viewer, which scrolls to a single one or the start of a range but
// cannot follow a list of them.
const normalizeNumbers = (numbers) => (numbers || '').trim().replace(/\s*[-–]\s*/g, '-').replace(/\s*,\s*/g, ', ');
const firstNumber = (numbers) => {
  const match = /\d+/.exec(numbers || '');
  return match ? match[0] : '';
};

// Spreadsheet citations link to the AI service's viewer, which wraps the file's
// S3 url: https://ai-graph.bimmatch.com/documents/spreadsheet#url=<encoded>&sheet=..&rows=..
// Returns the wrapped url, or the url itself when it wraps nothing.
const unwrapViewerUrl = (url) => {
  const wrapped = /[#?&]url=([^&#]+)/.exec(url || '');
  if (!wrapped) return url;
  try {
    return decodeURIComponent(wrapped[1]);
  } catch {
    return url;
  }
};

const UNVERIFIED_REF_HTML = '<span class="chat-ref-unverified" dir="auto">unverified citation</span> ';

// Replaces a document citation with its reference chip. A spreadsheet citation
// names a sheet and rows instead of pages; the viewer opens that sheet at its
// first cited row.
function toReplaceDocLinks({ source: sourceRaw, sheet: sheetRaw, rows: rowsRaw, pages: pagesRaw, url: directUrl }) {

  const source = (sourceRaw || '').trim();
  if (!source) return '';

  const sheet = sheetRaw || '';
  const pages = normalizeNumbers(pagesRaw);
  const rows = normalizeNumbers(rowsRaw);

  // Answers can cite files from any env's bucket (qa/staging/production), so
  // derive Bucket/Key from the url itself: https://s3.<region>.amazonaws.com/<bucket>/<key>
  // The url is usually presigned, so the query string (X-Amz-*) is not part of the Key.
  // The Key stays URL-encoded; /custom/pdf-viewer decodes it.
  const s3Match = (unwrapViewerUrl(directUrl) || '').match(/^https?:\/\/s3[.-][\w.-]+\.amazonaws\.com\/([^/?#]+)\/([^?#]+)/i)
  const fileAttr = s3Match ? { Bucket: s3Match[1], Key: s3Match[2] } : { Bucket: '', Key: '' }

  // Chat history comes back with citations already rewritten by our own backend
  // to the presigning route, which carries the document id rather than a bucket
  // key. The viewer opens by id just as well.
  const docIdMatch = (directUrl || '').match(/^\/apis\/docs\/([^/]+)\/file$/i)
  const docId = docIdMatch ? docIdMatch[1] : ''
  const canOpen = Boolean(fileAttr.Key || docId)

  // The chip is kept short; the tooltip carries the full citation.
  const rowsLabel = rows && `rows ${rows}`;
  const displaySource = stripExtension(source.replace(/^\.+/, '')).substring(0, 16);
  const label = [displaySource, ...(sheet ? [sheet.substring(0, 16), rowsLabel] : [pages])].filter(Boolean).join(',');
  const fullLabel = [source, ...(sheet ? [sheet, rowsLabel] : [pages && `page ${pages}`])].filter(Boolean).join(', ');

  // A citation with no usable URL still names its source; it just cannot open,
  // so it carries no data-type and the click handler ignores it.
  return `<span class="ref-link-chat ${(canOpen ? "ref-link-valid" : "ref-link-invalid")}"
            title="${sanitizeText(fullLabel)}"
            data-file="${sanitizeText(source)}"
            data-page="${sanitizeText(firstNumber(pages))}"
            data-pages="${sanitizeText(pages)}"
            data-sheet="${sanitizeText(sheet)}"
            data-rows="${sanitizeText(rows)}"
            data-row="${sanitizeText(firstNumber(rows))}"
            data-url="${sanitizeText(directUrl || '')}"
            data-bucket="${sanitizeText(fileAttr.Bucket)}"
            data-doc-id="${sanitizeText(docId)}"
            data-type="${canOpen ? 'doc' : ''}"
            dir="auto"
            data-key="${sanitizeText(encodeURIComponent(fileAttr.Key))}"
            >${sanitizeText(label)}</span> `;
}

// Extracts unique object ids referenced as `[Obj: 12345](link)` in a chat response.
// Returns an array of numeric ids (deduped, ordered by first occurrence).
export function extractMentionedObjectIds(text) {
  if (!text) return [];
  const regex = /\[Obj:\s*([^\]]+)\]\([^)]+\)/gi;
  const seen = new Set();
  const out = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    const id = Number(String(match[1]).trim());
    if (Number.isFinite(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

// replacing format with element link
function toReplaceElementLinks(_, objectId, link) {

  if (!objectId) {
    return ''
  }

  const label = [objectId].filter(Boolean).join(', ');

  return `<span class="ref-link-chat"
            data-objectId="${sanitizeText(objectId)}"
            data-type="element"
            dir="auto"
            >${sanitizeText(label)}</span> `;
} 

// const PDF_URL_MAP_QA = {
//   "511.005.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/511.005.pdf",
//   "511.007-1-1.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/511.007-1-1.pdf",
//   "511.007-1.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/511.007-1.pdf",
//   "512.011-1.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/512.011-1.pdf",
//   "512.007-2.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/512.007-2.pdf",
//   "512.004.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/512.004.pdf",
//   "511.006.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/511.006.pdf",
//   "512.009-1.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/653fbe80-e4c5-11ed-95e8-fdb8a484b2c4/docs/512.009-1.pdf",
//   "מדריך למתכנן - תכנון ציוד שאיבה בקידוחים.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/cf5f6380-f96d-11ed-829a-3560aa7f7b38/docs/מדריך למתכנן - תכנון ציוד שאיבה בקידוחים.pdf",
//   "מבחני שדה למשאבות.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/cf5f6380-f96d-11ed-829a-3560aa7f7b38/docs/מבחני שדה למשאבות.pdf",
//   "נוהל פירוק והרכבת משאבות במתקנים.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/cf5f6380-f96d-11ed-829a-3560aa7f7b38/docs/נוהל פירוק והרכבת משאבות במתקנים.pdf",
//   "אחסון והובלת חלקי מפוקים לקידוחים.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/cf5f6380-f96d-11ed-829a-3560aa7f7b38/docs/אחסון והובלת חלקי מפוקים לקידוחים.pdf",
//   "היכל ומגדל המשפט קובץ ב - קובץ עבודה.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/e90b67c0-fbe7-11ee-9f3c-0d0d8e692821/docs/היכל ומגדל המשפט קובץ ב - קובץ עבודה.pdf",
//   "היכל ומגדל המשפט קובץ ג - קובץ עבודה.pdf": "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/e90b67c0-fbe7-11ee-9f3c-0d0d8e692821/docs/היכל ומגדל המשפט קובץ ג - קובץ עבודה.pdf",
//   "היכל ומגדל המשפט קובץ ד - קובץ עבודה.pdf" : "https://s3.eu-central-1.amazonaws.com/qa.bimmatch-storage.eu-central-1/organizations/e90b67c0-fbe7-11ee-9f3c-0d0d8e692821/docs/היכל ומגדל המשפט קובץ ד - קובץ עבודה.pdf"
// }

// const PDF_URL_MAP_PROD = {
//   "511.005.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/511.005.pdf",
//   "511.007-1-1.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/511.007-1-1.pdf",
//   "511.007-1.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/511.007-1.pdf",
//   "512.011-1.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/512.011-1.pdf",
//   "512.007-2.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/512.007-2.pdf",
//   "512.004.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/512.004.pdf",
//   "511.006.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/511.006.pdf",
//   "512.009-1.pdf": "https://s3.eu-central-1.amazonaws.com/staging.bimmatch-storage.eu-west-1/organizations/b096dee0-9e16-11ed-a264-b1b0cf445ec6/docs/512.009-1.pdf",
// }

// function normFileKey(name) {
//   return (name || "")
//     .replace(/\.pdf$/i, "")
//     .replace(/[\s\-\u200f\u200e]/g, "")
//     .toLowerCase();
// }

// function resolveUrlForFile(fileName) {
//   if (!fileName) return null;
//   // 1) exact
//   console.log("fileName", fileName);

//   const pdfURLs = (docEnv === "production") ? PDF_URL_MAP_PROD : PDF_URL_MAP_QA
  
//   if (pdfURLs[fileName]) return pdfURLs[fileName];

//   // 2) normalized (handles RTL marks / dashes / spaces / case)
//   const key = normFileKey(fileName) + ".pdf";
//   for (const k of Object.keys(pdfURLs)) {
//     if (normFileKey(k) + ".pdf" === key) return pdfURLs[k];
//   }
//   return null;
// }